/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useEffect, useMemo } from 'react';

const STORAGE_KEY = 'lincal-language';
const SUPPORTED_LANGUAGES = ['en', 'de'];
const DEFAULT_LANGUAGE = 'en';

/**
 * Detect the user's preferred language from browser settings
 * @returns {string} Language code ('en' or 'de')
 */
function detectLanguage() {
  // Check localStorage first
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
    return stored;
  }

  // Check browser language
  const browserLang = navigator.language || navigator.userLanguage;
  if (browserLang) {
    // Extract base language (e.g., 'de-DE' -> 'de')
    const baseLang = browserLang.split('-')[0].toLowerCase();
    if (SUPPORTED_LANGUAGES.includes(baseLang)) {
      return baseLang;
    }
  }

  return DEFAULT_LANGUAGE;
}

/**
 * Get the locale string for date formatting
 * @param {string} lang - Language code
 * @returns {string} Locale string for Intl APIs
 */
function getLocale(lang) {
  const localeMap = {
    en: 'en-US',
    de: 'de-DE',
  };
  return localeMap[lang] || 'en-US';
}

export const LanguageContext = createContext(null);

/**
 * Language provider component
 * Wraps the app to provide language context for i18n
 */
export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(detectLanguage);

  // Persist language changes to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const setLanguage = (lang) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguageState(lang);
    }
  };

  const value = useMemo(() => ({
    language,
    locale: getLocale(language),
    setLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
  }), [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export default LanguageContext;
